import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Badge } from '@h2/ui';
import { Tabs, TabsList, TabsTrigger } from '@h2/ui';
import { ConfigurationStepper } from './ConfigurationStepper';
import { ConfigurationScroll } from './ConfigurationScroll';
import { ConfigurationManifest } from './ConfigurationManifest';
import { useConfigurationStore } from '../../stores/useConfigurationStore';
import { Configuration } from '@h2/types';

interface WorkflowContainerProps {
  workId: string;
  configuration: Configuration;
  onComplete: () => void;
  onCancel: () => void;
  title?: string;
  description?: string;
  showModeSelector?: boolean;
  defaultViewMode?: 'stepper' | 'scroll' | 'manifest';
  cancelButtonText?: string;
  completeButtonText?: string;
}

export const WorkflowContainer: React.FC<WorkflowContainerProps> = ({
  workId,
  configuration,
  onComplete,
  onCancel,
  title = '작업 설정',
  description = '새로운 작업을 위한 상세 설정을 진행합니다.',
  showModeSelector = true,
  defaultViewMode = 'stepper',
  cancelButtonText = '취소',
  completeButtonText = '완료'
}) => {
  const [viewMode, setViewMode] = useState<'stepper' | 'scroll' | 'manifest'>(defaultViewMode);
  
  const handleViewModeChange = (mode: 'stepper' | 'scroll' | 'manifest') => {
    setViewMode(mode);
    setStoreViewMode(mode);
  };
  
  const {
    setConfiguration,
    setViewMode: setStoreViewMode,
    formData,
    isDirty,
    submitConfiguration,
    resetForm
  } = useConfigurationStore();

  // Configuration 설정
  useEffect(() => {
    setConfiguration(configuration);
  }, [configuration]);

  const handleSubmit = async () => {
    try {
      await submitConfiguration();
      onComplete();
    } catch (error) {
      console.error('설정 제출 실패:', error);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const confirmed = window.confirm('변경사항이 있습니다. 정말 취소하시겠습니까?');
      if (!confirmed) return;
    }
    resetForm();
    onCancel();
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'stepper':
        return <ConfigurationStepper onComplete={onComplete} completeButtonText={completeButtonText} />;
      case 'scroll':
        return <ConfigurationScroll />;
      case 'manifest':
        return <ConfigurationManifest />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-none mx-auto p-6">
      {/* 헤더 with 모드 전환 */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                {title}
                <Badge variant="outline">Work ID: {workId}</Badge>
                {isDirty && (
                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                    수정됨
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {description}
              </CardDescription>
            </div>

            <div className="flex items-center gap-3">
              {showModeSelector && (
                <Tabs value={viewMode} onValueChange={(value) => handleViewModeChange(value as any)}>
                  <TabsList>
                    <TabsTrigger value="stepper">스테퍼</TabsTrigger>
                    <TabsTrigger value="scroll">스크롤</TabsTrigger>
                    <TabsTrigger value="manifest">매니페스트</TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {viewMode !== 'stepper' && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={handleCancel}
                  >
                    {cancelButtonText}
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                  >
                    {completeButtonText}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {renderContent()}
    </div>
  );
};
